import { Injectable, Injector, WritableSignal, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { WidgetConfig } from '@livechat/widget-angular';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { environment } from 'src/environments/environment';
import { SendbirdChatApi } from '../../apis/sendbird-chat.api';
import { AccountInforData } from '../../interfaces/account.interface';
import { LangvarService } from '../../service/langvar.service';
import { LocalStorageService } from '../../service/localstorage.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerServiceService {
  private readonly zendeskUidFieldId = '38265055928087';
  private zendeskScrollFixBound = false;

  constructor(
    private localStorageService: LocalStorageService,
    private appService: AppService,
    private injector: Injector,
    private langvarService: LangvarService,
    private sendbirdChatApi: SendbirdChatApi,
  ) {}

  chatType: 'liveChat' | 'zendesk' = 'zendesk';
  chatReady: WritableSignal<boolean> = signal(false);

  liveChatConfig: WidgetConfig = {
    visibility: 'hidden',
    license: '',
    group: '',
    customerName: undefined,
    customerEmail: undefined,
    sessionVariables: undefined,
  };

  /**
   * 初始化客服
   *
   * @param lang
   */
  init(lang: string) {
    // 设置客服类型
    // if (['zh-cn', 'vi', 'th'].includes(lang)) {
    //   this.chatType = 'liveChat';
    // } else {
    //   this.chatType = 'zendesk';
    // }

    switch (this.chatType) {
      case 'liveChat':
        this.initLiveChat();
        break;
      case 'zendesk':
        this.initZendesk();
        break;
      default:
        break;
    }
  }

  /**
   * 设置客服变量
   *
   * @param userInfo 用户信息(未登录为null)
   */
  setVariables(userInfo: AccountInforData | null = null) {
    switch (this.chatType) {
      case 'liveChat':
        this.setLiveChatVar(userInfo);
        break;
      case 'zendesk':
        if (!userInfo) {
          this.resetZendeskToGuest();
          return;
        }

        this.loginZendeskUser(
          async () => {
            const resp = await firstValueFrom(this.sendbirdChatApi.getZendeskJwt());
            if (!resp.data) {
              throw new Error('Zendesk JWT is empty');
            }

            return resp.data;
          },
          () => this.setZendeskMessagingUid(userInfo),
        );
        break;
      default:
        break;
    }
  }

  /** 初始化livechat客服 */
  initLiveChat() {
    const config = this.appService.tenantConfig$.value?.config;
    this.liveChatConfig = {
      ...this.liveChatConfig,
      license: config?.liveChatLicense || '',
      group: JSON.parse(config?.liveChatGroup || '{}')[this.appService.languageCode] ?? '',
    };
  }

  /** 初始化Zendesk客服 */
  initZendesk() {
    const config = this.appService.tenantConfig$.value?.config;
    // m1有做一個開關 zendeskEnabled
    const enable = 'true' || (config?.['zendeskEnabled'] as string);
    const zendeskUrl = config?.['zendeskUrl'] as string;
    if (!zendeskUrl || enable != 'true') return;
    this.appService.loadExternalScript(zendeskUrl, 'ze-snippet', () => {
      try {
        window.zE('messenger:set', 'locale', this.langvarService.zELang);
        window.zE(() => {
          this.bindZendeskMobileScrollFix();
          this.chatReady.set(true);
        });
      } catch (e) {
        console.error(e);
      }
    });
  }

  /**
   * 设置LiveChat客服变量
   *
   * @param info
   */
  setLiveChatVar(info: AccountInforData | null = null) {
    const contact = this.localStorageService.csTouristsContact;
    const userInfo = info ?? this.localStorageService.localUserInfo;
    const customerName = userInfo?.userName || userInfo?.uid || contact || undefined;
    const customerEmail = contact || undefined;
    const customerUid = userInfo?.uid || undefined;
    const sessionVariables: { username?: string; uid?: string; email?: string; version: string } = {
      version: environment.version,
    };

    if (customerName) sessionVariables.username = customerName;
    if (customerUid) sessionVariables.uid = customerUid;
    if (customerEmail) sessionVariables.email = customerEmail;

    this.liveChatConfig.customerName = customerName;
    this.liveChatConfig.customerEmail = customerEmail;
    this.liveChatConfig.sessionVariables = sessionVariables;
  }

  /**
   * 设置Zendesk客服变量（目前无用到，备用）
   *
   * @param userInfo
   */
  setZendeskVar(userInfo: AccountInforData | null = null) {
    if (userInfo) {
      //
    }
    const conversationFields: { id: string; value: string | number }[] = [];
    const obs = toObservable(this.chatReady, { injector: this.injector }).subscribe(x => {
      if (x) {
        window.zE('messenger:set', 'conversationFields', conversationFields);
        obs.unsubscribe();
      }
    });
  }

  /*=========================================================
                          Zendesk
  =========================================================*/
  /** H5 開關 Zendesk 時同步 body class，並在關閉後清掉殘留的 scroll lock 樣式 */
  private bindZendeskMobileScrollFix() {
    if (!window.zE || this.zendeskScrollFixBound) return;

    window.zE('messenger:on', 'open', () => {
      document.body.classList.add('zendesk-messenger-open');
    });

    window.zE('messenger:on', 'close', () => {
      document.body.classList.remove('zendesk-messenger-open');
      this.restoreMobileScrollAfterZendesk();
    });

    this.zendeskScrollFixBound = true;
  }

  private restoreMobileScrollAfterZendesk() {
    if (!document.body.classList.contains('is-mobile')) return;

    const body = document.body;
    const docEl = document.documentElement;

    body.style.position = '';
    body.style.top = '';
    body.style.width = '';
    body.style.overflow = '';
    body.style.overscrollBehavior = '';
    docEl.style.overflow = '';

    const content = document.querySelector('.basic-layout .content') as HTMLElement | null;
    if (!content) return;

    content.style.position = '';
    content.style.top = '';
    content.style.left = '';
    content.style.width = '';
    content.style.height = '';
    content.style.overflow = '';
    content.style.touchAction = '';
  }

  private runWhenZendeskReady(task: () => void) {
    if (this.chatReady()) {
      task();
      return;
    }

    const obs = toObservable(this.chatReady, { injector: this.injector }).subscribe(ready => {
      if (!ready) return;
      task();
      obs.unsubscribe();
    });
  }

  loginZendeskUser(getJwt: () => Promise<string>, onSuccess?: () => void) {
    this.runWhenZendeskReady(() => {
      if (!window.zE) return;

      window.zE(
        'messenger',
        'loginUser',
        async (callback: (jwt: string) => void) => {
          try {
            const jwt = await getJwt();
            if (!jwt) {
              console.error('Zendesk JWT is empty');
              this.resetZendeskToGuest();
              return;
            }
            callback(jwt);
          } catch (error) {
            this.resetZendeskToGuest();
            console.error('Failed to fetch Zendesk JWT', error);
          }
        },
        (error: unknown) => {
          if (error) {
            this.resetZendeskToGuest();
            console.error('Zendesk loginUser failed', error);
            return;
          }
          onSuccess?.();
        },
      );
    });
  }

  logoutZendeskUser(onSuccess?: () => void) {
    this.runWhenZendeskReady(() => {
      if (!window.zE) return;

      window.zE('messenger', 'logoutUser', () => {
        onSuccess?.();
      });
    });
  }

  resetZendeskToGuest() {
    this.logoutZendeskUser(() => {
      this.setZendeskMessagingUid(null);
    });
  }

  /**
   * 傳送 uid 給 Zendesk
   *
   * @param userInfo
   */
  setZendeskMessagingUid(userInfo: AccountInforData | null = null): void {
    const uid = userInfo?.uid ?? '';
    const conversationFields: { id: string; value: string | number | boolean }[] = [
      { id: this.zendeskUidFieldId, value: uid },
    ];
    const applyConversationFields = () => {
      if (!window.zE) return;
      window.zE('messenger:set', 'conversationFields', conversationFields);
    };

    if (this.chatReady()) {
      applyConversationFields();
      return;
    }

    const obs = toObservable(this.chatReady, { injector: this.injector }).subscribe(ready => {
      if (!ready) return;
      applyConversationFields();
      obs.unsubscribe();
    });
  }
}
